import {
  type GarmentGeometry,
  type GarmentGeometryButton,
  type GarmentGeometrySeamLine,
  type GarmentGeometrySewingOperation,
  type SceneEntity,
  decodeGarmentGeometry,
  meshOrientedSpline,
} from '@seddi/scene';
import { type BufferGeometry, type LoadingManager, Sphere, Vector3 } from '@seddi/three';

import { create as createGeometry } from '../core/bufferGeometry';
import type { VertexDataDesc } from '../core/bufferTypes';
import { load as loadFile } from '../loaders/binaryAssetLoader';
import { createLinesGeometry } from '../renderables/seamRenderable';
import type {
  GarmentGeometryButtonGfx,
  GarmentGeometryDesc,
  GarmentGeometryGfx,
  GarmentGeometrySewingOperationGfx,
} from './garmentGeometryTypes';

type LoadOptions = { frustumCulling: boolean };

/**
 * Load garment geometry.
 * @param url = Resource URL.
 * @param children - Scene children.
 * @param manager - Loading manager.
 * @param config - Loading config.
 */
export const load = (() => {
  return async (
    url: string,
    children: SceneEntity[],
    manager: LoadingManager,
    config: LoadOptions,
  ): Promise<GarmentGeometryGfx> => {
    const bytes = await loadFile(url, manager);
    const data = decodeGarmentGeometry(bytes, children);

    // TODO: configure no-sewing-operation-lines, no-stitches.
    const desc = adaptDesc(data);

    // Create empty.
    const res: GarmentGeometryGfx = {
      buttons: new Map(),
      pieces: new Map(),
      sewingOperations: new Map(),
      virtualPieces: new Map(),
    };

    // Load descriptors as buffer geometries.
    for (const b of desc.buttons) {
      res.buttons.set(b.id, loadButton(b, config));
    }
    for (const p of desc.pieces) {
      // Not setting a bounding sphere placeholder to avoid blending issues.
      res.pieces.set(p.id, createGeometry(p));
    }
    for (const s of desc.sewingOperations) {
      res.sewingOperations.set(s.id, loadSewingOperation(s, config));
    }
    for (const p of desc.virtualPieces) {
      // Not setting set a bounding sphere placeholder to avoid blending issues.
      res.virtualPieces.set(p.id, createGeometry(p));
    }

    return res;
  };
})();

/**
 * Adapt garment geometry data.
 * @param src - Garment geometry data.
 */
const adaptDesc = (src: GarmentGeometry): GarmentGeometryDesc => {
  const meshes = src.meshes.map((mesh) => ({
    indices: mesh.indices,
    positions: mesh.position3D,
    uvs: mesh.position2D,
    verticesOffset: mesh.verticesOffset,
  }));
  const pieces = src.pieces.map((piece) => ({
    id: piece.id,
    indices: piece.indices,
    positions: piece.position3D,
    uvs: piece.position2D,
  }));
  const virtualPieces = src.virtualPieces.map((piece) => ({
    id: piece.id,
    indices: piece.indices,
    positions: piece.position3D,
    uvs: piece.position2D,
  }));
  const res = { ...src, meshes, pieces, virtualPieces };
  return res;
};

/**
 * Create geometry and apply configuration options.
 * @param data - Geometry data.
 * @param config - Loading config.
 */
const createGeometryWithOptions = (data: VertexDataDesc, config: LoadOptions): BufferGeometry => {
  const geometry = createGeometry(data);
  // Avoid bounding sphere calculation.
  if (!config.frustumCulling) {
    geometry.boundingSphere = boundingSpherePlaceholder;
  }
  return geometry;
};

/**
 * Load garment geometry button.
 * @param src - Garment geometry button descriptor.
 * @param config - Loading config.
 */
const loadButton = (src: GarmentGeometryButton, config: LoadOptions): GarmentGeometryButtonGfx => {
  const stitch = src.stitch.map((s) => {
    const geometryData = meshOrientedSpline(s, 3);
    const geometry = createGeometryWithOptions(geometryData, config);
    return { geometry, name: s.name, thread: s.thread };
  });
  const res = { stitch, transform: src.transform };
  return res;
};

/**
 * Load garment geometry sewing operation.
 * @param src - Garment geometry sewing operation.
 * @param config - Loading config.
 */
const loadSewingOperation = (
  src: GarmentGeometrySewingOperation,
  config: LoadOptions,
): GarmentGeometrySewingOperationGfx => {
  const geometry = loadSewingOperationGeometry(src.lines, config);
  const stitches = src.stitches.map((s) => {
    const vdata = meshOrientedSpline(s, 3);
    const geometry = createGeometryWithOptions(vdata, config);
    return { geometry, name: s.name, order: s.order, thread: s.thread };
  });
  const res = { geometry, stitches };
  return res;
};

/**
 * Load garment geometry sewing operation lines geometry.
 * @param src - Garment geometry sewing operation lines descriptor.
 * @param config - Loading config.
 */
const loadSewingOperationGeometry = (src: GarmentGeometrySeamLine[], config: LoadOptions) => {
  // Transform lines vertex data.
  const sides = src.map((side) => {
    return side.segments.map((segment) => {
      const { indices, position3D } = segment;
      return copyVerticesFromIndices(indices, position3D);
    });
  });

  // Create lines geometry.
  const geometry = createLinesGeometry(sides);
  // Avoid bounding sphere calculation.
  if (!config.frustumCulling) {
    geometry.boundingSphere = boundingSpherePlaceholder;
  }

  return geometry;
};

// ------ GEOMETRY DETAILS ------

/**
 * [loadSewingOperationGeometry] Copy vertex positions from indices.
 * @param indices - Piece geometry indices.
 * @param positions - Piece geometry positions.
 * @returns The array of positions.
 */
const copyVerticesFromIndices = (indices: Uint32Array, positions: Float32Array): Float32Array => {
  const n = indices.length;
  const vertices = new Float32Array(n * 3);

  for (let i = 0; i < n; i += 1) {
    const v = indices[i];
    const v3 = v * 3;
    const i3 = i * 3;
    vertices[i3] = positions[v3];
    vertices[i3 + 1] = positions[v3 + 1];
    vertices[i3 + 2] = positions[v3 + 2];
  }

  return vertices;
};

// ------ PLACEHOLDERS ------

const boundingSpherePlaceholder = new Sphere(new Vector3(), Infinity);
